import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ReactComponent as CertIcon } from '../../../assets/icons/device-hub/medal-star.svg';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import BatteryLoader from '../../../shared-components/battery-loader/BatteryLoader';
import MyButton from '../../../shared-components/button/Button';
import CheckBoxButtons from '../../../shared-components/check-box-buttons/CheckBoxButtons';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SelectField from '../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import getFirstSubdomain from '../../../shared-functions/DomainsplitFunction';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import deviceApi from '../../../utils/api/device-hub';

import AddBrandDrawer from './drawers/AddBrandDrawer';
import AddConnectorRatingDrawer from './drawers/AddConnectorRatingDrawer';
import AddModelDrawer from './drawers/AddModelDrawer';

import '../scss/add-device.scss';

// ── Option lists ────────────────────────────────────────────────────────────────
const CURRENT_TYPE_OPTIONS = [
  { value: 'ac', label: 'AC' },
  { value: 'dc', label: 'DC' },
  { value: 'hybrid', label: 'Hybrid' },
];

const INSTALLATION_TYPE_OPTIONS = [
  { value: 'wall', label: 'Wall Mounted' },
  { value: 'pedestal', label: 'Pedestal' },
];

const NETWORK_OPTIONS = [
  { value: '4g', label: '4G/LTE' },
  { value: 'wifi', label: 'Wi-Fi' },
  { value: 'eth', label: 'Ethernet' },
];

const PROTOCOL_OPTIONS = ['OCPP 1.6', 'OCPP 2.0.1', 'OCPP 2.1'];

const CONNECTOR_TYPE_OPTIONS = [
  { value: 'CCS1', label: 'CCS1' },
  { value: 'CCS2', label: 'CCS2' },
  { value: 'Type2', label: 'Type2' },
  { value: 'Type1', label: 'Type1' },
  { value: 'CHAdeMO', label: 'CHAdeMO' },
  { value: 'NACS', label: 'NACS' },
  { value: 'J1772', label: 'J1772' },
];
const CONNECTOR_FORMAT_OPTIONS = [
  { value: 'socket', label: 'Socket' },
  { value: 'cable', label: 'Cable' },
];

const DISPLAY_TYPE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'lcd', label: 'LCD' },
  { value: 'led', label: 'LED' },
];

const CERTIFICATIONS = ['CE', 'UL', 'BIS', 'TUV', 'RoHS'];

// ── Types ────────────────────────────────────────────────────────────────────────
interface ConnectorRow {
  id: number;
  currentType: string;
  connectorType: string;
  connectorFormat: string;
  maxVoltage: string;
  maxPower: string;
  maxCurrent: string;
  maxPowerOutput: string;
  connectorRating: string;
  quantity: number;
}

interface AddDeviceFormState {
  deviceId: string;
  brand: string;
  model: string;
  currentType: string;
  skuNumber: string;
  installationType: string;
  networkConnectivity: string[];
  plugAndCharge: 'yes' | 'no';
  protocols: string;
  connectors: ConnectorRow[];
  maxPower: string;
  ratedPowerOutput: string;
  maxVoltage: string;
  maxCurrent: string;
  efficiencyRating: string;
  operationTemperature: string;
  weight: string;
  displayType: string;
  certifications: string[];
  firmwareVersion: string;
}

// ── Sub-components ───────────────────────────────────────────────────────────────

/**
 * Info tooltip icon component
 * @returns {any} The rendered SVG icon
 */
function InfoIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 20 20"
      fill="currentColor"
      style={{ color: '#aaa', flexShrink: 0 }}>
      <path
        fillRule="evenodd"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Trash icon component for connector row deletion
 * @returns {any} The rendered SVG icon
 */
function TrashIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/**
 * Protocol icon component (chip/integrated circuit style)
 * @returns {any} The rendered SVG icon
 */
function ProtocolIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <rect x="4" y="4" width="16" height="16" rx="2" ry="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="1" x2="9" y2="4" />
      <line x1="15" y1="1" x2="15" y2="4" />
      <line x1="9" y1="20" x2="9" y2="23" />
      <line x1="15" y1="20" x2="15" y2="23" />
      <line x1="20" y1="9" x2="23" y2="9" />
      <line x1="20" y1="15" x2="23" y2="15" />
      <line x1="1" y1="9" x2="4" y2="9" />
      <line x1="1" y1="15" x2="4" y2="15" />
    </svg>
  );
}

/**
 * Generates initial state for a single connector row
 * @returns {any} Initial connector object
 */
const initialConnector = (): ConnectorRow => ({
  id: Date.now(),
  currentType: '',
  connectorType: '',
  connectorFormat: '',
  maxVoltage: '',
  maxCurrent: '',
  maxPower: '',
  maxPowerOutput: '',
  connectorRating: '',
  quantity: 1,
});

/**
 * Generates initial state for the entire add device form
 * @returns {any} Initial form state
 */
const initialForm = (): AddDeviceFormState => ({
  deviceId: '',
  brand: '',
  model: '',
  currentType: '',
  skuNumber: '',
  installationType: '',
  networkConnectivity: [],
  plugAndCharge: 'no',
  protocols: 'OCPP 1.6',
  connectors: [initialConnector()],
  ratedPowerOutput: '',
  maxVoltage: '',
  maxCurrent: '',
  maxPower: '',
  efficiencyRating: '',
  operationTemperature: '',
  weight: '',
  displayType: '',
  certifications: [],
  firmwareVersion: '',
});

interface ConnectorRatingOption {
  value: string;
  label: string;
  currentType?: string;
  name?: string;
  capacityKW?: string;
  labelNode?: React.ReactNode;
}

/**
 * AddDevice component - Full page view for adding a new EV charging device.
 * @returns {any} The rendered AddDevice page.
 */
function AddDevice() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [form, setForm] = useState<AddDeviceFormState>(initialForm);
  const [loading, setLoading] = useState(isEdit);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, boolean>>({});

  // Dynamic options state
  const [brandOptions, setBrandOptions] = useState<{ value: string; label: string }[]>([]);
  const [modelOptions, setModelOptions] = useState<{ value: string; label: string }[]>([]);
  const [connectorRatingOptions, setConnectorRatingOptions] = useState<ConnectorRatingOption[]>([]);

  // Modals state
  const [brandModalOpen, setBrandModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);


  /**
   * Fetches active brand list from API and updates brand options state.
   * @returns {any} []>} A promise that resolves to brand options array.
   */
  const loadBrands = () => {
    return deviceApi
      .fetchBrands({ paginate: false, status: ['approved', 'pending'] })
      .then((response) => {
        const data = response.data?.results || response.data || [];
        const brands = data.map((brand: any) => {
          const isGlobal = !!brand.source;
          return {
            value: brand._id,
            label: brand.name,
            labelNode: (
              <div className="nd-brand-option">
                <span>{brand.name}</span>
                <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                  {isGlobal ? 'Global' : 'Local'}
                </span>
              </div>
            )
          };
        });
        setBrandOptions(brands);
        return brands;
      })
      .catch((error) => {
        console.error('Failed to fetch brands:', error);
        return [];
      });
  };

  /**
   * Fetches active models list for a specific brand.
   * @param {string} [brandIdOrName] - The brand identifier or name.
   * @param {Array<{ value: string; label: string }>} [overrideBrands] - Optional override brand list.
   * @returns {Promise<{ value: string; label: string }[]>} A promise resolving to the model options array.
   */
  const loadModels = (
    brandIdOrName?: string,
    overrideBrands?: { value: string; label: string }[],
  ) => {
    if (!brandIdOrName) {
      setModelOptions([]);
      return Promise.resolve([]);
    }

    const availableBrands =
      overrideBrands && overrideBrands.length > 0 ? overrideBrands : brandOptions;

    const selectedBrand = availableBrands.find(
      (brand) =>
        brand.value === brandIdOrName ||
        brand.label.toLowerCase() === brandIdOrName.toLowerCase(),
    );

    if (!selectedBrand) {
      setModelOptions([]);
      return Promise.resolve([]);
    }

    // Clear old models immediately while fetching new models
    setModelOptions([]);

    return deviceApi
      .fetchModels({
        brandName: selectedBrand.label,
        paginate: false,
        status: ['approved', 'pending'],
      })
      .then((response) => {
        const data = response.data?.results || response.data || [];

        const models = data.map((model: any) => ({
          value: model._id,
          label: model.name,
        }));

        setModelOptions(models);

        return models;
      })
      .catch((error) => {
        console.error('Failed to fetch models:', error);
        setModelOptions([]);
        return [];
      });
  };
  /**
   * Fetches dynamic connector ratings from the API and updates options state.
   * @returns {Promise<ConnectorRatingOption[]>} A promise that resolves to rating options array.
   */
  const loadConnectorRatings = (): Promise<ConnectorRatingOption[]> => {
    return deviceApi
      .connecterRateList({ paginate: false })
      .then((response) => {
        const data = response?.data?.results || response?.data || [];
        const options: ConnectorRatingOption[] = data.map((item: any) => {
          const isGlobal = !!item?.source;

          if (item?.value && item?.label) {
            return {
              value: item?.value,
              label: item?.label,
              currentType: item?.currentType,
              name: item?.name,
              capacityKW: item?.capacityKW !== undefined ? String(item.capacityKW) : undefined,
              labelNode: (
                <div className="nd-brand-option">
                  <span>{item?.label}</span>
                  <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                    {isGlobal ? 'Global' : 'Local'}
                  </span>
                </div>
              ),
            };
          }
          const maxPower = item?.capacityKW !== undefined ? String(item.capacityKW) : '';
          const unit = item?.unit || 'kW';
          const label =
            item?.name ||
            (item?.currentType ? `${item.currentType} ${maxPower} ${unit}` : `${maxPower} ${unit}`);
          const value = item?._id || item?.id || '';
          const { currentType, name } = item;
          return {
            value,
            label,
            currentType,
            name,
            capacityKW: maxPower,
            labelNode: (
              <div className="nd-brand-option">
                <span>{label}</span>
                <span className={`nd-badge ${isGlobal ? 'global' : 'local'}`}>
                  {isGlobal ? 'Global' : 'Local'}
                </span>
              </div>
            ),
          };
        });

        setConnectorRatingOptions(options);
        return options;
      })
      .catch((error) => {
        console.error('Failed to fetch connector ratings:', error);
        setConnectorRatingOptions([]);
        return [];
      });
  };

  useEffect(() => {
    // Load brands and connector ratings first, then if editing, fetch device data
    Promise.all([loadBrands(), loadConnectorRatings()]).then(([brands, ratingOptions]) => {
      if (isEdit) {
        setLoading(true);
        deviceApi
          .fetchDeviceById(id)
          .then((response) => {
            const device = response.data;

            // 1. Match Brand by brandName or brandId
            const matchedBrand = brands.find(
              (b: any) =>
                (device.brandName && b.label.toLowerCase() === device.brandName.toLowerCase()) ||
                (device.brandId &&
                  b.value ===
                    (typeof device.brandId === 'object' ? device.brandId?._id : device.brandId)),
            );
            const brandValue = matchedBrand ? matchedBrand.value : '';
            const brandNameValue = matchedBrand ? matchedBrand.label : device.brandName || '';

            // 2. Load Models for the matched brand to match modelName or modelId
            const modelPromise =
              brandValue || brandNameValue
                ? loadModels(brandValue || brandNameValue, brands)
                : Promise.resolve([]);

            modelPromise.then((models) => {
              // 3. Match Model by modelName or modelId
              const matchedModel = models.find(
                (m: any) =>
                  (device.modelName && m.label.toLowerCase() === device.modelName.toLowerCase()) ||
                  (device.modelId &&
                    m.value ===
                      (typeof device.modelId === 'object' ? device.modelId?._id : device.modelId)),
              );
              let modelValue = '';
              if (matchedModel) {
                modelValue = matchedModel.value;
              } else if (typeof device.modelId === 'object') {
                modelValue = device.modelId?._id || '';
              } else {
                modelValue = device.modelId || '';
              }

              // 4. Group Connectors and match rating value
              const groupedConnectors = device.connectorInformation?.length
                ? (Object.values(
                    device.connectorInformation.reduce(
                      (accumulator: Record<string, ConnectorRow>, connector: any) => {
                        let ratingValue = '';
                        let matchedRating: ConnectorRatingOption | undefined;

                        if (
                          connector.connectorRating &&
                          typeof connector.connectorRating === 'object'
                        ) {
                          const ratingObject = connector.connectorRating;
                          matchedRating = ratingOptions.find((option: ConnectorRatingOption) => {
                            const nameMatch =
                              option.name &&
                              ratingObject.name &&
                              option.name.toLowerCase() === ratingObject.name.toLowerCase();
                            const typeMatch =
                              option.currentType &&
                              ratingObject.currentType &&
                              option.currentType.toLowerCase() ===
                                ratingObject.currentType.toLowerCase();
                            const capMatch =
                              option.capacityKW !== undefined &&
                              ratingObject.capacityKW !== undefined &&
                              String(option.capacityKW) === String(ratingObject.capacityKW);
                            return nameMatch && typeMatch && capMatch;
                          });

                          if (!matchedRating && ratingObject._id) {
                            matchedRating = ratingOptions.find(
                              (option: ConnectorRatingOption) => option.value === ratingObject._id,
                            );
                          }

                          if (matchedRating) {
                            ratingValue = matchedRating.value;
                          } else {
                            ratingValue = ratingObject._id || ratingObject.name || '';
                          }
                        } else if (connector.connectorRating) {
                          ratingValue = connector.connectorRating;
                          matchedRating = ratingOptions.find(
                            (option: ConnectorRatingOption) =>
                              option.value === ratingValue || option.label === ratingValue,
                          );
                          if (matchedRating) {
                            ratingValue = matchedRating.value;
                          }
                        }

                        const key = JSON.stringify({
                          currentType: connector.currentType,
                          connectorType: connector.connectorType,
                          connectorFormat: connector.connectorFormat,
                          maxVoltage: connector.maxVoltage,
                          maxCurrent: connector.maxCurrent,
                          maxPowerOutput: connector.maxPowerOutput,
                          connectorRating: ratingValue,
                        });

                        if (!accumulator[key]) {
                          accumulator[key] = {
                            id: Date.now() + Math.random(),
                            currentType: connector.currentType?.toLowerCase() || '',
                            connectorType: connector.connectorType || '',
                            connectorFormat: connector.connectorFormat || '',
                            maxVoltage: connector.maxVoltage?.toString() || '',
                            maxPower: connector.maxPower?.toString() || '',
                            maxCurrent: connector.maxCurrent?.toString() || '',
                            maxPowerOutput: connector.maxPowerOutput?.toString() || '',
                            connectorRating: ratingValue,
                            quantity: 1,
                          };
                        } else {
                          accumulator[key].quantity += 1;
                        }

                        return accumulator;
                      },
                      {},
                    ),
                  ) as ConnectorRow[])
                : [initialConnector()];

              if (device) {
                // Mapping network connectivity back to form values
                let selectedNetworks: string[] = [];
                if (Array.isArray(device.networkConnectivity)) {
                  selectedNetworks = device.networkConnectivity.map((value: string) => {
                    const normalized = value.toLowerCase();
                    if (normalized === '4g/lte' || normalized === '4g') return '4g';
                    if (normalized === 'wifi') return 'wifi';
                    if (normalized === 'ethernet' || normalized === 'eth') return 'eth';
                    return value;
                  });
                } else if (typeof device.networkConnectivity === 'string') {
                  const normalized = device.networkConnectivity.toLowerCase();
                  if (normalized === '4g/lte' || normalized === '4g') selectedNetworks = ['4g'];
                  else if (normalized === 'wifi') selectedNetworks = ['wifi'];
                  else if (normalized === 'ethernet' || normalized === 'eth')
                    selectedNetworks = ['eth'];
                  else if (device.networkConnectivity)
                    selectedNetworks = [device.networkConnectivity];
                }

                const protocolValue = Array.isArray(device.protocolSupport)
                  ? device.protocolSupport[0]
                  : device.protocolSupport;

                setForm({
                  deviceId: device.serialNumber || '',
                  brand: brandValue,
                  model: modelValue,
                  currentType: device.currentType ? device.currentType.toLowerCase() : '',
                  skuNumber: device.skuNumber || '',
                  installationType: device.installationType || '',
                  networkConnectivity: selectedNetworks,
                  plugAndCharge: device.plugAndCharge ? 'yes' : 'no',
                  protocols:
                    PROTOCOL_OPTIONS.find(
                      (opt) => opt.toLowerCase() === protocolValue?.toLowerCase(),
                    ) ||
                    protocolValue ||
                    '',
                  connectors: groupedConnectors,
                  ratedPowerOutput:
                    device.specifications?.powerOutput?.toString() ||
                    device.specifications?.ratedPowerOutput?.toString() ||
                    '',
                  maxVoltage: device.specifications?.maxVoltage || '',
                  maxPower: device.specifications?.maxPower || '',
                  maxCurrent: device.specifications?.maxCurrent || '',
                  efficiencyRating: device.specifications?.efficiencyRating || '',
                  operationTemperature: device.specifications?.operationTemperature || '',
                  weight: device.specifications?.weight || '',
                  displayType: device.specifications?.displayType || '',
                  certifications: (
                    device.certifications ||
                    device.specifications?.certifications ||
                    []
                  ).map(
                    (c: string) =>
                      CERTIFICATIONS.find((opt) => opt.toLowerCase() === c.toLowerCase()) || c,
                  ),
                  firmwareVersion:
                    device.firmwareVersion || device.specifications?.firmwareVersion || '',
                });
              }
            });
          })
          .catch((error) => {
            console.error('Failed to fetch device data:', error);
            dispatch(
              setNotificationMessage({
                status: true,
                type: 'error',
                message: 'Failed to fetch device details',
                code: 0,
              }),
            );
          })
          .finally(() => setLoading(false));
      }
    });
  }, [dispatch, id, isEdit]);

  // Fetch models whenever brand selection changes
  useEffect(() => {
    if (!form.brand) {
      setModelOptions([]);
      return;
    }

    loadModels(form.brand);
  }, [form.brand, brandOptions]);

  /**
   * Save device (create or update)
   */
  const handleSave = () => {
    // Validate all required fields
    const newErrors: Record<string, boolean> = {};
    let hasValidationError = false;

    // 1. Device ID
    if (!form.deviceId) {
      newErrors.deviceId = true;
      hasValidationError = true;
    }
    // 2. Brand
    if (!form.brand) {
      newErrors.brand = true;
      hasValidationError = true;
    }
    // 3. Model
    if (!form.model) {
      newErrors.model = true;
      hasValidationError = true;
    }
    // 4. Current Type
    if (!form.currentType) {
      newErrors.currentType = true;
      hasValidationError = true;
    }
    // 5. Protocols (Must select at least one protocol)
    if (!form.protocols || form.protocols.length === 0) {
      newErrors.protocols = true;
      hasValidationError = true;
    }
    // 6. Network Connectivity
    if (!form.networkConnectivity || form.networkConnectivity.length === 0) {
      newErrors.networkConnectivity = true;
      hasValidationError = true;
    }
    // 7. Plug & Charge
    if (!form.plugAndCharge) {
      newErrors.plugAndCharge = true;
      hasValidationError = true;
    } 
    // 8. Rated Power Output
    if (!form.ratedPowerOutput) {
      newErrors.ratedPowerOutput = true;
      hasValidationError = true;
    }
    // 9. Connector row fields
    form.connectors.forEach((c) => {
      if (!c.currentType) {
        newErrors[`currentType-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.connectorType) {
        newErrors[`connectorType-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.connectorFormat) {
        newErrors[`connectorFormat-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.maxVoltage) {
        newErrors[`maxVoltage-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.maxCurrent) {
        newErrors[`maxCurrent-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.maxPower) {
        newErrors[`maxPower-${c.id}`] = true;
        hasValidationError = true;
      }
      if (!c.connectorRating) {
        newErrors[`connectorRating-${c.id}`] = true;
        hasValidationError = true;
      }
    });


    if (hasValidationError) {
      setErrors(newErrors);
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: 'Please fill in all required fields.',
          code: 0,
        }),
      );
      return;
    }

    // Mapping network connectivity values to match backend Joi valid values (all lowercase as required by the backend)
    const networkMap: Record<string, string> = {
      '4g': '4g/lte',
      wifi: 'wifi',
      eth: 'ethernet',
    };

    const selectedBrandName = brandOptions.find((b) => b.value === form.brand)?.label || form.brand;
    const selectedModelName = modelOptions.find((m) => m.value === form.model)?.label || form.model;

    // 1. Build the raw payload with all potential fields according to the schema
    const rawPayload: any = {
      serialNumber: form.deviceId,
      brandName: selectedBrandName,
      modelName: selectedModelName,
      skuNumber: form.skuNumber,
      tenantName: getFirstSubdomain() || '',
      currentType: form.currentType
        ? (form.currentType.toUpperCase() as 'AC' | 'DC' | 'HYBRID')
        : undefined,
      installationType: form.installationType,
      networkConnectivity: form.networkConnectivity.map((value) => networkMap[value] || value),
      plugAndCharge: form.plugAndCharge === 'yes',
      protocolSupport: form.protocols.length > 0 ? form.protocols.toLowerCase() : undefined,
      connectorInformation: form.connectors.flatMap((c) => {
        const ratingOpt = connectorRatingOptions.find(
          (opt) => opt.value === c.connectorRating || opt.label === c.connectorRating,
        );
        const connectorRatingObject = ratingOpt
          ? {
              name: ratingOpt.name || '',
              currentType: ratingOpt.currentType || '',
              capacityKW: ratingOpt.capacityKW ? String(ratingOpt.capacityKW) : '',
            }
          : undefined;

        const connectorObject = {
          currentType: c.currentType?.toUpperCase(),
          connectorType: c.connectorType,
          connectorFormat: c.connectorFormat,
          maxVoltage: c.maxVoltage ? Number(c.maxVoltage) : undefined,
          maxCurrent: c.maxCurrent ? Number(c.maxCurrent) : undefined,
          maxPower: c.maxPower ? Number(c.maxPower) : undefined,
          connectorRating: connectorRatingObject,
          unit: 'kW',
        };
        // If quantity > 1, the backend expects multiple items in the array for each physical connector
        return Array(c.quantity).fill(connectorObject);
      }),
      specifications: {
        powerOutput: form.ratedPowerOutput
          ? Number(form.ratedPowerOutput)
          : undefined,
        maxVoltage: form.maxVoltage,
        maxCurrent: form.maxCurrent,
        efficiencyRating: form.efficiencyRating,
        operationTemperature: form.operationTemperature,
        weight: form.weight,
        displayType: form.displayType,
      },
      firmwareVersion: form.firmwareVersion,
      certifications:
        form.certifications.length > 0
          ? form.certifications.map((c) => c.toLowerCase())
          : undefined,
    };

    /**
     * Recursively removes undefined, null, empty strings, empty arrays, and empty objects.
     * This ensures the payload strictly matches the "only send fields with values" requirement.
     * @param {any} value - The value
     */
    const clean = (value: any): any => {
      // Handle Arrays
      if (Array.isArray(value)) {
        const cleanedArray = value
          .map(clean)
          .filter((item) => item !== undefined && item !== null && item !== '');
        return cleanedArray.length > 0 ? cleanedArray : undefined;
      }
      // Handle Objects
      if (value && typeof value === 'object' && !(value instanceof Date)) {
        const cleanedObject = Object.fromEntries(
          Object.entries(value)
            .map(([key, item]) => [key, clean(item)])
            .filter(([, item]) => item !== undefined && item !== null && item !== ''),
        );
        return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
      }
      // Handle primitive values
      return value === '' || value === null ? undefined : value;
    };

    const cleanPayload = clean(rawPayload) || {};

    setLoading(true);
    const apiCall = isEdit
      ? deviceApi.updateDevice(id || '', cleanPayload)
      : deviceApi.addDevice(cleanPayload);

    apiCall
      .then(() => {
        setShowSuccess(true);
      })
      .catch((error: any) => {
        dispatch(
          setNotificationMessage({
            status: true,
            type: 'error',
            message: error?.response?.data?.message || 'Something went wrong!',
            code: 0,
          }),
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // ── Field helpers ──────────────────────────────────────────────────────────────
  /**
   * Updates a string field in the form state
   * @param {any} name - field key
   * @param {any} value - new value
   */
  const handleField = (name: string, value: string | number | string[]) => {
    const decimalFields = ['ratedPowerOutput', 'maxVoltage', 'maxCurrent'];
    if (decimalFields.includes(name)) {
      const inputValue = value.toString();

      const regex = name === 'ratedPowerOutput' ? /^\d*\.?\d{0,3}$/ : /^\d*\.?\d{0,2}$/;
      if (!regex.test(inputValue)) {
        return;
      }
    }
    setForm((previous) => {
      const updatedForm = { ...previous, [name]: value };
      // Clear model if brand changes
      if (name === 'brand') {
        updatedForm.model = '';
      }
      // If currentType changes and is not hybrid, replicate to all connectors
      if (name === 'currentType') {
        if (value !== 'hybrid') {
          updatedForm.connectors = updatedForm.connectors.map((c) => ({
            ...c,
            currentType: value as string,
          }));
        }
      }
      return updatedForm;
    });

    // Clear validation error when field is updated
    setErrors((previousErrors) => {
      if (previousErrors[name] || (name === 'brand' && previousErrors.model)) {
        const nextErrors = { ...previousErrors };
        delete nextErrors[name];
        if (name === 'brand') {
          delete nextErrors.model;
        }
        return nextErrors;
      }
      return previousErrors;
    });
  };

  /**
   * Toggles a protocol chip on/off
   * @param {any} protocol - protocol string
   */
  const toggleProtocol = (protocol: string) => {
    setForm((previousState) => {
      const nextProtocol = previousState.protocols === protocol ? '' : protocol;

      setErrors((previousErrors) => {
        if (previousErrors.protocols) {
          const nextErrors = { ...previousErrors };
          delete nextErrors.protocols;
          return nextErrors;
        }
        return previousErrors;
      });

      return {
        ...previousState,
        protocols: nextProtocol,
      };
    });
  };

  /**
   * Toggles a certification chip on/off
   * @param {any} cert - certification label
   */
  const toggleCert = (cert: string) => {
    setForm((previous) => ({
      ...previous,
      certifications: previous.certifications.includes(cert)
        ? previous.certifications.filter((certItem) => certItem !== cert)
        : [...previous.certifications, cert],
    }));
  };

  // ── Connector helpers ──────────────────────────────────────────────────────────
  /**
   * Adds a new blank connector row
   */
  const addConnector = () => {
    const totalConnectors = form.connectors.reduce((sum, connector) => sum + connector.quantity, 0);

    if (totalConnectors >= 2) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: 'Maximum connector limit reached (2).',
          code: 0,
        }),
      );
      return;
    }

    setForm((previous) => {
      const newConnector = initialConnector();
      if (previous.currentType && previous.currentType !== 'hybrid') {
        newConnector.currentType = previous.currentType;
      }
      return {
        ...previous,
        connectors: [...previous.connectors, newConnector],
      };
    });
  };

  /**
   * Removes a connector row by id
   * @param {any} connectorId - row id
   */
  const removeConnector = (connectorId: number) => {
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.filter((connector) => connector.id !== connectorId),
    }));
  };

  /**
   * Updates a field inside a specific connector row
   * @param {any} connectorId - connector row id
   * @param {any} field - field key within the connector row
   * @param {any} value - new value
   */
  const updateConnector = (
    connectorId: number,
    field: keyof ConnectorRow,
    value: string | number,
  ) => {
    if (field === 'maxVoltage' || field === 'maxCurrent' || field === 'maxPower') {
      const inputValue = value.toString();

      const regex = (field === 'maxCurrent' || field === 'maxPower') ? /^\d*\.?\d{0,3}$/ : /^\d*\.?\d{0,2}$/;

      if (!regex.test(inputValue)) {
        return;
      }
    }
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.map((connector) =>
        connector.id === connectorId ? { ...connector, [field]: value } : connector,
      ),
    }));

    // Clear error for this specific field if it exists
    const errorKey = `${field}-${connectorId}`;
    setErrors((previousErrors) => {
      if (previousErrors[errorKey]) {
        const nextErrors = { ...previousErrors };
        delete nextErrors[errorKey];
        return nextErrors;
      }
      return previousErrors;
    });
  };

  /**
   * Increments connector quantity by 1
   * @param {any} connectorId - connector row id
   */
  const incrementQty = (connectorId: number) => {
    const totalConnectors = form.connectors.reduce((sum, connector) => sum + connector.quantity, 0);

    if (totalConnectors >= 2) {
      dispatch(
        setNotificationMessage({
          status: true,
          type: 'error',
          message: 'Maximum connector limit reached (2).',
          code: 0,
        }),
      );
      return;
    }
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.map((connector) =>
        connector.id === connectorId
          ? { ...connector, quantity: connector.quantity + 1 }
          : connector,
      ),
    }));
  };

  /**
   * Decrements connector quantity, minimum 1
   * @param {any} connectorId - connector row id
   */
  const decrementQty = (connectorId: number) => {
    setForm((previous) => ({
      ...previous,
      connectors: previous.connectors.map((connector) =>
        connector.id === connectorId && connector.quantity > 1
          ? { ...connector, quantity: connector.quantity - 1 }
          : connector,
      ),
    }));
  };

  /**
   * Returns the appropriate label for the save/update button based on state
   * @returns {string} The button label
   */
  const getSaveButtonLabel = () => {
    if (loading) return 'Saving...';
    return isEdit ? 'Update' : 'Save';
  };

  if (isEdit && loading) {
    return (
      <div className="loader">
        <BatteryLoader />
      </div>
    );
  }

  return (
    <div className="new-device-page-wrap">
      <div className="new-device-panel">
        {/* ── Top Bar ── */}
        <div className="new-device-topbar">
          <h2 className="new-device-title">{isEdit ? 'Edit Device' : 'Add Device'}</h2>
          <div className="new-device-actions">
            <MyButton label="Cancel" buttonType="error" onClickFunc={() => navigate(-1)} />
            <MyButton
              label={getSaveButtonLabel()}
              buttonType="submit"
              onClickFunc={handleSave}
              disableButton={loading}
              loading={loading}
            />
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div className="new-device-body">
          {/* ━━━ Section 1: Device Type & Model ━━━ */}
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">Device Type &amp; Model</p>
                <p className="nd-section-subtitle">
                  Configure the core identity, brand, and network connectivity settings for this
                  charging unit.
                </p>
              </div>
              <div className="nd-header-buttons" style={{ display: 'flex', gap: '12px' }}>
                <MyButton
                  label="+ Add Brand"
                  buttonType="primary-outline"
                  onClickFunc={() => setBrandModalOpen(true)}
                  disableButton={!permissionCheck('devicesBrandsAdd')}
                  hoverLabel={
                    !permissionCheck('devicesBrandsAdd')
                      ? 'You do not have permission to add a brand'
                      : ''
                  }
                />
                <MyButton
                  label="+ Add Model"
                  buttonType="primary-outline"
                  onClickFunc={() => setModelModalOpen(true)}
                  disableButton={!permissionCheck('devicesModelsAdd')}
                  hoverLabel={
                    !permissionCheck('devicesModelsAdd')
                      ? 'You do not have permission to add a model'
                      : ''
                  }
                />
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <span className="nd-label">
                  Device ID <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Unique identifier used to register and track the charger in the platform.
                    </span>
                  </span>
                </span>
                <CustomInput
                  type="text"
                  name="deviceId"
                  placeholder="Enter Device ID"
                  value={form.deviceId}
                  onChangeValue={handleField}
                  error={!!errors.deviceId}
                  errorMessage="This field is required."
                  valueRequired
                  characterLimit={100}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  Brand <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Manufacturer or brand name of the EV charger hardware.
                    </span>
                  </span>
                </span>
                <SelectField
                  name="brand"
                  options={brandOptions}
                  placeholder="Choose Brand"
                  selectedValue={form.brand}
                  onChangeValue={handleField}
                  error={!!errors.brand}
                  errorMessage="This field is required."
                  valueRequired
                  enableSearch
                  hideLabelNodeInSelected
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  Model <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Specific model name or number assigned by the manufacturer.
                    </span>
                  </span>
                </span>
                <SelectField
                  name="model"
                  options={modelOptions}
                  placeholder="Choose Model"
                  selectedValue={form.model}
                  onChangeValue={handleField}
                  disabled={!form.brand}
                  error={!!errors.model}
                  errorMessage="This field is required."
                  valueRequired
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  Current Type <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Indicates whether the charger supports AC (Alternating Current) or DC (Direct
                      Current) charging.
                    </span>
                  </span>
                </span>
                <SelectField
                  name="currentType"
                  options={CURRENT_TYPE_OPTIONS}
                  placeholder="Choose Current Type"
                  selectedValue={form.currentType}
                  onChangeValue={handleField}
                  error={!!errors.currentType}
                  errorMessage="This field is required."
                  valueRequired
                />
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <span className="nd-label">
                  SKU Number
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Stock Keeping Unit identifier used for inventory, procurement, and product
                      classification.
                    </span>
                  </span>
                </span>
                <CustomInput
                  type="text"
                  name="skuNumber"
                  placeholder="Eg: A8B-T360-CE"
                  value={form.skuNumber}
                  onChangeValue={handleField}
                  characterLimit={100}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  Installation Type
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Defines where or how the charger is installed, such as Wall-mounted, Pedestal,
                      or Portable.
                    </span>
                  </span>
                </span>
                <SelectField
                  name="installationType"
                  options={INSTALLATION_TYPE_OPTIONS}
                  placeholder="Choose Installation Type"
                  selectedValue={form.installationType}
                  onChangeValue={handleField}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  Firmware Version
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Current software/firmware version running on the charger device.
                    </span>
                  </span>
                </span>
                <CustomInput
                  type="text"
                  name="firmwareVersion"
                  placeholder="Enter Firmware Version"
                  value={form.firmwareVersion}
                  onChangeValue={handleField}
                  characterLimit={100}
                />
              </div>
              <div className="nd-field">
                <span className="nd-label">
                  Plug &amp; Charge <span className="required">*</span>
                  <span className="nd-col-tooltip-wrap">
                    <InfoIcon />
                    <span className="nd-col-tooltip-text">
                      Indicates whether the charger supports automatic vehicle authentication and
                      charging initiation without RFID or app interaction.
                    </span>
                  </span>
                </span>
                <div className={`nd-radio-group${errors.plugAndCharge ? ' error' : ''}`}>
                  <label className="nd-radio-label" htmlFor="plug-yes">
                    <input
                      id="plug-yes"
                      type="radio"
                      name="plugAndCharge"
                      value="yes"
                      checked={form.plugAndCharge === 'yes'}
                      onChange={() => {
                        setForm((previous) => ({ ...previous, plugAndCharge: 'yes' }));
                        setErrors((previousErrors) => {
                          if (previousErrors.plugAndCharge) {
                            const nextErrors = { ...previousErrors };
                            delete nextErrors.plugAndCharge;
                            return nextErrors;
                          }
                          return previousErrors;
                        });
                      }}
                    />
                    Yes
                  </label>
                  <label className="nd-radio-label" htmlFor="plug-no">
                    <input
                      id="plug-no"
                      type="radio"
                      name="plugAndCharge"
                      value="no"
                      checked={form.plugAndCharge === 'no'}
                      onChange={() => {
                        setForm((previous) => ({ ...previous, plugAndCharge: 'no' }));
                        setErrors((previousErrors) => {
                          if (previousErrors.plugAndCharge) {
                            const nextErrors = { ...previousErrors };
                            delete nextErrors.plugAndCharge;
                            return nextErrors;
                          }
                          return previousErrors;
                        });
                      }}
                    />
                    No
                  </label>
                </div>
              </div>
            </div>

            <div className="nd-connectivity-protocol-cert-row">
              <div className="nd-field">
                <CheckBoxButtons
                  inputLabel="Network Connectivity"
                  name="networkConnectivity"
                  list={NETWORK_OPTIONS}
                  value={form.networkConnectivity}
                  onChangeValue={(name, value) => handleField(name, value as string[])}
                  error={!!errors.networkConnectivity}
                  errorMessage="This field is required."
                  valueRequired
                />
              </div>
              {/* Protocol Support */}
              <div className="nd-field">
                <p className="nd-label">
                  Protocol Support <span className="required">*</span>
                </p>

                <div
                  className={`nd-chip-group${errors.protocols ? ' error' : ''}`}
                  role="group"
                  aria-label="Protocol Support Selection">
                  {PROTOCOL_OPTIONS.map((protocol) => (
                    <button
                      key={protocol}
                      type="button"
                      className={`nd-chip${form.protocols === protocol ? ' active' : ''}`}
                      onClick={() => toggleProtocol(protocol)}>
                      <ProtocolIcon />
                      {protocol}
                    </button>
                  ))}
                </div>
              </div>

              <div className="nd-field">
                <p className="nd-label">Certifications</p>
                <div className="nd-chip-group">
                  {CERTIFICATIONS.map((cert) => (
                    <button
                      key={cert}
                      type="button"
                      className={`nd-chip nd-chip-cert${form.certifications.includes(cert) ? ' active' : ''}`}
                      onClick={() => toggleCert(cert)}>
                      <CertIcon />
                      {cert}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ━━━ Section 3: Specifications ━━━ */}
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">Specifications</p>
                <p className="nd-section-subtitle">
                  Specify technical parameters including voltage, current, and environmental
                  ratings.
                </p>
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="ratedPowerOutput"
                  inputLabel="Power Output (kW)"
                  placeholder="Eg: 12"
                  value={form.ratedPowerOutput}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg="Maximum 0 to 1000 allowed."
                  decimalLimit={3}
                  error={!!errors.ratedPowerOutput}
                  errorMessage="This field is required."
                  valueRequired
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="maxVoltage"
                  inputLabel="Max Voltage (V)"
                  placeholder="Eg: 12"
                  value={form.maxVoltage}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg="Maximum 0 to 1000 allowed."
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="maxCurrent"
                  inputLabel="Max Current (A)"
                  placeholder="Eg: 32"
                  value={form.maxCurrent}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg="Maximum 0 to 1000 allowed."
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="efficiencyRating"
                  inputLabel="Efficiency Rating (%)"
                  placeholder="Eg: 95"
                  value={form.efficiencyRating}
                  onChangeValue={handleField}
                  maxValue={100}
                  limitErrorMsg="Maximum 1 to 100 allowed."
                  decimalLimit={2}
                />
              </div>
            </div>

            <div className="nd-grid-4">
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="operationTemperature"
                  inputLabel="Operation Temperature"
                  placeholder="Enter Operation Temperature"
                  value={form.operationTemperature}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg="Maximum 0 to 1000 allowed."
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <CustomInput
                  type="number"
                  name="weight"
                  inputLabel="Weight (Kg)"
                  placeholder="Eg: 12"
                  value={form.weight}
                  onChangeValue={handleField}
                  maxValue={1000}
                  limitErrorMsg="Maximum 0 to 1000 allowed."
                  decimalLimit={2}
                />
              </div>
              <div className="nd-field">
                <SelectField
                  name="displayType"
                  inputLabel="Display Type"
                  options={DISPLAY_TYPE_OPTIONS}
                  placeholder="Choose Display Type"
                  selectedValue={form.displayType}
                  onChangeValue={handleField}
                />
              </div>
            </div>
          </div>

          {/* ━━━ Section 2: Connector Information ━━━ */}
          <div className="nd-section">
            <div className="nd-section-header">
              <div>
                <p className="nd-section-title">
                  Connector Information <span style={{ color: '#ff4d4f' }}>*</span>
                </p>
                <p className="nd-section-subtitle">
                  General identification and configuration details of the EV charger device.
                </p>
              </div>
              <div className="nd-header-buttons">
                <MyButton
                  label="+ Add Connector Rating"
                  buttonType="primary-outline"
                  onClickFunc={() => setRatingModalOpen(true)}
                  disableButton={!permissionCheck('devicesRatingAdd')}
                  hoverLabel={
                    !permissionCheck('devicesRatingAdd')
                      ? 'You do not have permission to add a connector rating'
                      : ''
                  }
                />
              </div>
            </div>

            {/* Column headers */}
            <div className="nd-connector-header" role="row">
              <span role="columnheader">
                Current Type <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Indicates whether the connector supports AC or DC charging current.
                  </span>
                </span>
              </span>
              <span role="columnheader">
                Connector Rating <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Electrical rating or capacity specification of the connector assembly.
                  </span>
                </span>
              </span>

              <span role="columnheader">
                Max Power (kW)<span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Maximum power output supported by the connector.
                  </span>
                </span>
              </span>
              <span role="columnheader">
                Max Voltage (V)<span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Maximum voltage supported through the connector.
                  </span>
                </span>
              </span>
              <span role="columnheader">
                Max Current (A)<span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Maximum charging current supported by the connector.
                  </span>
                </span>
              </span>
              <span role="columnheader">
                Connector Type <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Physical connector standard supported by the charger, such as CCS2, CHAdeMO,
                    Type 2, or GB/T.
                  </span>
                </span>
              </span>
              <span role="columnheader">
                Connector Format <span style={{ color: '#ff4d4f' }}>*</span>
                <span className="nd-col-tooltip-wrap">
                  <InfoIcon />
                  <span className="nd-col-tooltip-text">
                    Physical connector standard supported by the charger, such as CCS2, CHAdeMO,
                    Type 2, or GB/T.
                  </span>
                </span>
              </span>

              <span role="columnheader" aria-label="Actions" />
            </div>

            {/* Connector rows */}
            {form.connectors.map((connector) => (
              <div key={connector.id} className="nd-connector-row">
                <div
                  className={`nd-read-only-box ${errors[`currentType-${connector.id}`] ? 'error' : ''}`}
                  aria-label="Current Type">
                  {connector.currentType ? connector.currentType.toUpperCase() : ' '}
                </div>
                <SelectField
                  name={`connectorRating-${connector.id}`}
                  aria-label="Connector Rating"
                  options={connectorRatingOptions.filter((rating) => {
                    if (form.currentType === 'hybrid') {
                      return true;
                    }
                    return (
                      rating.currentType?.toLowerCase() === connector.currentType?.toLowerCase()
                    );
                  })}
                  placeholder="Choose Rating"
                  selectedValue={connector.connectorRating}
                  onChangeValue={(_, value) => {
                    updateConnector(connector.id, 'connectorRating', value);
                    const selectedRating = connectorRatingOptions.find(
                      (opt) => opt.value === value || opt.label === value,
                    );
                    if (selectedRating) {
                      if (selectedRating.capacityKW) {
                        updateConnector(connector.id, 'maxPower', selectedRating.capacityKW);
                      }
                      if (form.currentType === 'hybrid' && selectedRating.currentType) {
                        updateConnector(
                          connector.id,
                          'currentType',
                          selectedRating.currentType.toLowerCase(),
                        );
                      }
                    }
                  }}
                  error={!!errors[`connectorRating-${connector.id}`]}
                  errorMessage="This field is required."
                  hideLabelNodeInSelected
                />

                <CustomInput
                  name={`maxPower-${connector.id}`}
                  type="number"
                  placeholder="Eg: 50"
                  value={connector.maxPower}
                  onChangeValue={() => {}}
                  error={!!errors[`maxPower-${connector.id}`]}
                  errorMessage="This field is required."
                  fontSize="small"
                  hint={false}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      kW
                    </span>
                  }
                  maxValue={1000}
                  limitErrorMsg="Maximum 1 to 1000 allowed."
                  decimalLimit={3}
                />
                <CustomInput
                  name={`maxVoltage-${connector.id}`}
                  type="number"
                  placeholder="Eg: 12"
                  value={connector.maxVoltage}
                  onChangeValue={(_, value) => updateConnector(connector.id, 'maxVoltage', value)}
                  error={!!errors[`maxVoltage-${connector.id}`]}
                  errorMessage="This field is required."
                  fontSize="small"
                  hint={false}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      V
                    </span>
                  }
                  maxValue={1000}
                  limitErrorMsg="Maximum 1 to 1000 allowed."
                  decimalLimit={2}
                />
                <CustomInput
                  name={`maxCurrent-${connector.id}`}
                  type="number"
                  placeholder="Eg: 12"
                  value={connector.maxCurrent}
                  onChangeValue={(_, value) => updateConnector(connector.id, 'maxCurrent', value)}
                  error={!!errors[`maxCurrent-${connector.id}`]}
                  errorMessage="This field is required."
                  fontSize="small"
                  hint={false}
                  svgIcon={
                    <span
                      className="suffix"
                      style={{ fontSize: '11px', color: '#9aa3b2', fontWeight: 600 }}>
                      A
                    </span>
                  }
                  maxValue={1000}
                  limitErrorMsg="Maximum 1 to 1000 allowed."
                  decimalLimit={3}
                />
                <SelectField
                  name={`connectorType-${connector.id}`}
                  aria-label="Connector Type"
                  options={CONNECTOR_TYPE_OPTIONS}
                  placeholder="Choose Connector Type"
                  selectedValue={connector.connectorType}
                  onChangeValue={(_, value) =>
                    updateConnector(connector.id, 'connectorType', value)
                  }
                  error={!!errors[`connectorType-${connector.id}`]}
                  errorMessage="This field is required."
                />
                <SelectField
                  name={`connectorFormat-${connector.id}`}
                  aria-label="Connector Format"
                  options={CONNECTOR_FORMAT_OPTIONS}
                  placeholder="Choose Format"
                  selectedValue={connector.connectorFormat}
                  onChangeValue={(_, value) =>
                    updateConnector(connector.id, 'connectorFormat', value)
                  }
                  error={!!errors[`connectorFormat-${connector.id}`]}
                  errorMessage="This field is required."
                />

                {/* Quantity controls + delete */}
                <div className="nd-qty-controls">
                  <button
                    type="button"
                    className="nd-qty-btn sub"
                    aria-label="Decrease Quantity"
                    onClick={() => decrementQty(connector.id)}>
                    −
                  </button>
                  <span className="nd-qty-val">{connector.quantity}</span>
                  <button
                    type="button"
                    className="nd-qty-btn add"
                    aria-label="Increase Quantity"
                    onClick={() => incrementQty(connector.id)}>
                    +
                  </button>
                  <button
                    type="button"
                    className="nd-qty-btn del"
                    aria-label="Delete Connector"
                    onClick={() => removeConnector(connector.id)}
                    disabled={form.connectors.length === 1}>
                    <TrashIcon />
                  </button>
                </div>
              </div>
            ))}

            {/* Add Connector */}
            <div className="nd-add-connector">
              <button type="button" className="nd-link-btn" onClick={addConnector}>
                + Add Connector
              </button>
            </div>
          </div>
        </div>
      </div>

      <ModalBox
        open={showSuccess}
        title=""
        content={
          <SuccessNotification
            title={isEdit ? 'Device Updated Successfully' : 'Device Registered Successfully'}
            description={`The charging device has been ${isEdit ? 'updated' : 'added'} successfully.`}
            onNotificationClose={() => navigate(-1)}
          />
        }
        closeModal={() => setShowSuccess(false)}
        header={false}
        notification
      />

      <AddBrandDrawer
        open={brandModalOpen}
        onClose={() => setBrandModalOpen(false)}
        onBrandCreated={(brandId) => {
          loadBrands().then(() => {
            if (brandId) {
              setForm((previous) => ({ ...previous, brand: brandId, model: '' }));
            }
          });
        }}
      />

      <AddModelDrawer
        open={modelModalOpen}
        onClose={() => setModelModalOpen(false)}
        onModelCreated={(brandId, modelId, models) => {
          setModelOptions(models);
          setForm((previous) => ({ ...previous, brand: brandId, model: modelId }));
        }}
      />

      <AddConnectorRatingDrawer
        open={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        existingValues={connectorRatingOptions.map((opt) => opt.value)}
        onRatingCreated={(ratingId) => {
          loadConnectorRatings().then((ratings) => {
            if (ratingId) {
              const createdRating = ratings.find((r) => r.value === ratingId);
              if (createdRating) {
                setForm((previous) => {
                  const targetIndex = previous.connectors.findIndex(
                    (c) =>
                      !c.connectorRating &&
                      (!c.currentType ||
                        !createdRating.currentType ||
                        c.currentType.toLowerCase() === createdRating.currentType.toLowerCase() ||
                        previous.currentType === 'hybrid'),
                  );

                  const indexToUpdate = targetIndex !== -1 ? targetIndex : 0;

                  const updatedConnectors = previous.connectors.map((c, index) => {
                    if (index === indexToUpdate) {
                      return {
                        ...c,
                        connectorRating: createdRating.value,
                        maxPower: createdRating.capacityKW || c.maxPower,
                        currentType:
                          previous.currentType === 'hybrid' && createdRating.currentType
                            ? createdRating.currentType.toLowerCase()
                            : c.currentType || createdRating.currentType?.toLowerCase() || '',
                      };
                    }
                    return c;
                  });

                  return {
                    ...previous,
                    connectors: updatedConnectors,
                  };
                });

                setErrors((previousErrors) => {
                  const nextErrors = { ...previousErrors };
                  form.connectors.forEach((c) => {
                    delete nextErrors[`connectorRating-${c.id}`];
                    delete nextErrors[`maxPower-${c.id}`];
                    delete nextErrors[`currentType-${c.id}`];
                  });
                  return nextErrors;
                });
              }
            }
          });
        }}
      />
    </div>
  );
}

export default AddDevice;
